import { useGlobalEntityStore } from "@/utils/globalEntityStore";
import { Message, PostMessageParams } from "@cohesive/api-client";
import { ErrorMsg } from "@cohesive/ui-components";
import React, { useEffect, useMemo, useRef } from "react";
import { parseMessageHistory } from "./parseMessageHistory";
import { TurnView } from "./TurnView";

export type RetryHandler = (params: PostMessageParams) => void;

/**
 * Group a flat message list into turns. A turn starts at each user message (or
 * when the API's `turn_id` changes) and gathers the assistant rounds that
 * follow; messages without a `turn_id` fall back to user-message boundaries so
 * history predating the field still groups correctly.
 */
function groupIntoTurns(messages: Message[]): Message[][] {
  const turns: Message[][] = [];
  for (const m of messages) {
    const cur = turns[turns.length - 1];
    // Compare against the first message in the current turn that actually
    // carries a turn_id — not strictly cur[0]. A turn can lead with a message
    // that lacks the id (e.g. a legacy/optimistic user message) while later
    // messages carry it; keying off cur[0] alone would then ignore a turn_id
    // change and merge two exchanges into one turn.
    const curTurnId = cur?.find((x) => x.turn_id)?.turn_id;
    const startNew =
      !cur ||
      m.origin === "user" ||
      (!!m.turn_id && !!curTurnId && m.turn_id !== curTurnId);
    if (startNew) turns.push([m]);
    else cur.push(m);
  }
  return turns;
}

interface ChatHistoryProps {
  messages: Message[];
  onRetry: RetryHandler;
  lastUserMessageRef?: React.RefObject<HTMLDivElement | null>;
}

export const FailedMessage = ({
  errorMessage = "There was an issue processing your message.",
  handleRetry,
}: {
  errorMessage?: string;
  handleRetry?: () => void;
}) => {
  return (
    <div className="my-4 px-4">
      <ErrorMsg
        title="Something went wrong"
        text={errorMessage}
        onRetry={handleRetry}
      />
    </div>
  );
};

/**
 * Tail-of-conversation indicator. The only signal rendered here is the error
 * state; in-flight activity is shown inline by the live turn's rail header
 * (TurnView), which appears immediately as "Planning out work…" the moment a
 * turn starts and then snaps into the step-count / duration summary as the
 * model responds — so there is no separate blinking working signal here.
 */
const LastMessageIndicator = React.memo(
  ({
    lastMessage,
    lastUserMessage,
    handleRetry,
  }: {
    lastMessage: Message | undefined;
    lastUserMessage: Message | undefined;
    handleRetry: () => void;
  }) => {
    if (lastMessage?.status === "failed") {
      return (
        <FailedMessage
          handleRetry={lastUserMessage ? handleRetry : undefined}
        />
      );
    }
    return null;
  },
);

LastMessageIndicator.displayName = "LastMessageIndicator";

const ChatHistory: React.FC<ChatHistoryProps> = ({
  messages,
  onRetry,
  lastUserMessageRef,
}) => {
  const prevMessageCountRef = useRef(0);
  const globalEntityStore = useGlobalEntityStore(messages ?? []);

  useEffect(() => {
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const formattedMessages = useMemo(() => {
    return parseMessageHistory(messages);
  }, [messages]);

  const turns = useMemo(
    () => groupIntoTurns(formattedMessages),
    [formattedMessages],
  );

  const lastMessage = formattedMessages[formattedMessages.length - 1];
  const lastUserMessageIndex = formattedMessages.findLastIndex(
    (m) => m.origin === "user",
  );
  const lastUserMessage = formattedMessages[lastUserMessageIndex];

  const handleRetry = () => {
    if (onRetry && lastUserMessage?.status === "failed") {
      onRetry({
        id: crypto.randomUUID(),
        content: lastUserMessage.content,
        // Replay the original message's file references. Historical messages
        // carry `attached_files` instead of `file_record_ids`; those are no
        // longer an accepted POST channel, so retry-with-files only works
        // for messages authored after the file_records migration.
        file_record_ids: lastUserMessage.file_record_ids,
        referenced_entities: lastUserMessage.referenced_entities,
      });
    }
  };

  return (
    <div className="w-full sm:max-w-[776px] mx-auto scrollbar-hide">
      {turns.map((turn, i) => (
        <TurnView
          key={turn[0].id}
          turn={turn}
          entities={globalEntityStore}
          isLastTurn={i === turns.length - 1}
          lastUserMessageId={lastUserMessage?.id}
          lastUserMessageRef={lastUserMessageRef}
        />
      ))}

      <LastMessageIndicator
        lastMessage={lastMessage}
        lastUserMessage={lastUserMessage}
        handleRetry={handleRetry}
      />
    </div>
  );
};

export default React.memo(ChatHistory);
